import { useState, useEffect } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────
export interface TopClientRecord {
  contact_id: number;
  contact_name: string;
  client_tier: string;
  status: string;
  client_base_currency: string;

  financial_year: string;       // e.g. "FY2025-26"
  revenue_period: string;       // "Q1" | "Q2" | "Q3" | "Q4"
  fy_year: number;              // e.g. 2025

  revenue_fy_quarter: number;
  revenue_fy_quarter_client_base: number;
  invoice_count_fy_quarter: number;

  revenue_3m: number;
  revenue_3m_client_base: number;
  revenue_6m: number;
  revenue_6m_client_base: number;
  revenue_12m: number;
  revenue_12m_client_base: number;
  revenue_total: number;
  revenue_total_client_base: number;

  invoice_count_3m: number;
  invoice_count_6m: number;
  invoice_count_12m: number;
  invoice_count_total: number;

  avg_invoice_amount: number;
  avg_invoice_amount_client_base: number;

  first_invoice_date: string;
  last_invoice_date: string;
  relationship_days: number;

  rank_3m: number | null;
  rank_6m: number | null;
  rank_12m: number | null;
  rank_total: number | null;

  pct_of_org_revenue_3m: number;
  pct_of_org_revenue_6m: number;
  pct_of_org_revenue_12m: number;
  pct_of_org_revenue_total: number;
  pct_of_org_revenue_fy_quarter: number;

  concentration_risk: string;
  client_value_category: string;
  days_since_last_invoice: number;
}

export interface UseTopClientsRevenueResult {
  records: TopClientRecord[];
  loading: boolean;
  error: string | null;
}

// ─── Parser ───────────────────────────────────────────────────────────────────
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function parseRecord(r: any): TopClientRecord {
  const num = (v: unknown) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };
  const nullableNum = (v: unknown): number | null => {
    if (v == null || v === '' || v === 'null') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  return {
    contact_id:               num(r.contact_id),
    contact_name:             String(r.contact_name ?? ''),
    client_tier:              String(r.client_tier ?? ''),
    status:                   String(r.status ?? ''),
    client_base_currency:     String(r.client_base_currency ?? 'USD').trim().toUpperCase(),
    financial_year:           String(r.financial_year ?? ''),
    revenue_period:           String(r.revenue_period ?? ''),
    fy_year:                  num(r.fy_year),

    revenue_fy_quarter:             num(r.revenue_fy_quarter),
    revenue_fy_quarter_client_base: num(r.revenue_fy_quarter_client_base),
    invoice_count_fy_quarter:       num(r.invoice_count_fy_quarter),

    revenue_3m:               num(r.revenue_3m),
    revenue_3m_client_base:   num(r.revenue_3m_client_base),
    revenue_6m:               num(r.revenue_6m),
    revenue_6m_client_base:   num(r.revenue_6m_client_base),
    revenue_12m:              num(r.revenue_12m),
    revenue_12m_client_base:  num(r.revenue_12m_client_base),
    revenue_total:            num(r.revenue_total),
    revenue_total_client_base: num(r.revenue_total_client_base),

    invoice_count_3m:         num(r.invoice_count_3m),
    invoice_count_6m:         num(r.invoice_count_6m),
    invoice_count_12m:        num(r.invoice_count_12m),
    invoice_count_total:      num(r.invoice_count_total),

    avg_invoice_amount:             num(r.avg_invoice_amount),
    avg_invoice_amount_client_base: num(r.avg_invoice_amount_client_base),

    first_invoice_date:       String(r.first_invoice_date ?? ''),
    last_invoice_date:        String(r.last_invoice_date ?? ''),
    relationship_days:        num(r.relationship_days),

    rank_3m:    nullableNum(r.rank_3m),
    rank_6m:    nullableNum(r.rank_6m),
    rank_12m:   nullableNum(r.rank_12m),
    rank_total: nullableNum(r.rank_total),

    pct_of_org_revenue_3m:          num(r.pct_of_org_revenue_3m),
    pct_of_org_revenue_6m:          num(r.pct_of_org_revenue_6m),
    pct_of_org_revenue_12m:         num(r.pct_of_org_revenue_12m),
    pct_of_org_revenue_total:       num(r.pct_of_org_revenue_total),
    pct_of_org_revenue_fy_quarter:  num(r.pct_of_org_revenue_fy_quarter),

    concentration_risk:       String(r.concentration_risk ?? ''),
    client_value_category:    String(r.client_value_category ?? ''),
    days_since_last_invoice:  num(r.days_since_last_invoice),
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────
export function useTopClientsRevenue(): UseTopClientsRevenueResult {
  const [records, setRecords] = useState<TopClientRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const resp = await fetch('/json-data/top_clients_revenue.json');
        if (!resp.ok) throw new Error(`HTTP ${resp.status}: ${resp.statusText}`);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const raw: any[] = await resp.json();
        if (!cancelled) {
          setRecords(Array.isArray(raw) ? raw.map(parseRecord) : []);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Unknown error');
          setLoading(false);
        }
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  return { records, loading, error };
}
